import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";

export const TutorChat = ({ courseId }) => {
  const key = `tutor_${courseId}`;
  const [sessionId] = useState(() => localStorage.getItem(key) || (() => {
    const s = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(key, s); return s;
  })());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    api.get(`/courses/${courseId}/tutor/${sessionId}`).then(({ data }) => setMsgs(data)).catch(() => {});
  }, [courseId, sessionId]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const append = (piece) => setMsgs((m) => {
    const last = m[m.length - 1];
    return [...m.slice(0, -1), { ...last, content: last.content + piece }];
  });

  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/courses/${courseId}/tutor`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Tutor unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const payload = ev.replace(/^data: /, "");
          if (payload === "[DONE]") continue;
          const obj = JSON.parse(payload);
          append(obj.delta || obj.error || "");
        }
      }
    } catch (err) {
      append(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[#121721] border border-white/10 p-5 mt-6" data-testid="tutor-chat">
      <div className="flex items-center gap-2 text-white font-semibold mb-1"><Bot className="h-4 w-4 text-amber-400" /> Ask the course</div>
      <p className="text-xs text-slate-500 mb-4">An AI tutor that answers from this course's material. For anything else, email the instructor.</p>
      <div className="max-h-80 overflow-y-auto space-y-3 mb-3 pr-1">
        {msgs.map((m, i) => (
          <div key={i} data-testid={`tutor-msg-${i}`} className={`text-sm rounded-xl px-3.5 py-2.5 whitespace-pre-line ${
            m.role === "user" ? "bg-amber-500/10 text-amber-100 ml-8" : "bg-white/5 text-slate-200 mr-8"}`}>
            {m.content || <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input data-testid="tutor-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Can you explain step 2 again?"
          className="flex-1 min-w-0 bg-[#0B0E14] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500" />
        <button data-testid="tutor-send" disabled={busy} className="h-10 w-10 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 flex items-center justify-center disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};
